import {
  Box,
  Flex,
  Spinner,
  Text,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { useHookstate } from "@hookstate/core";
import { Artist, LayoutStyle } from "../../types";
import { ShowMoreSection } from "../../components/containers/ShowMoreSection";
import { useArtistComicsAPI } from "./hooks/useArtistComicsAPI";
import { useState, useEffect } from "react";
import { useInvalidateQueries } from "../../services/axiosInstance";
import { GET_ARTIST_COMICS_QUERY_KEY } from "./api/artistComicsApi";
import { SortMenus } from "../../components/sortMenus/SortMenus";
import { artistComicsFilterStore } from "./components/ArtistComicsFilters";
import {
  getArtist,
  useSlideInMenu,
} from "../../components/slideInMenu/hooks/useSlideInMenu";
import { useQuery, skipToken } from "@tanstack/react-query";
import { ArtistProfileComicsList } from "./components/ArtistProfileComicsList";
import { ArtistProfileHeader } from "./components/ArtistProfileHeader";
import { SlideInHeader } from "../../components/slideInMenu/components/SlideInHeader";
import { SLIDE_IN_MENUS } from "../root/constants";
import { appStore } from "../../hookstate/appStore";
import { useEntityCache } from "../../hooks/useEntityCache";
import { UnderlinedTab } from "../../components/tabs/UnderlinedTab";
import { NewBadge } from "../../components/buttons/NewBadge";
import { Bundle } from "../../types";
import { SlideInMenuBundlesList } from "../../components/slideInMenu/components/SlideInMenuBundlesList";
import { SealCheckIcon } from "../../components/icons/SealCheckIcon";
import { ReleaseTypeBanner } from "../../components/slideInMenu/components/ReleaseTypeBanner";

interface ArtistProfileProps {
  onCloseMenu?: () => void;
}

export function ArtistProfile({ onCloseMenu }: ArtistProfileProps) {
  const { artistId, goBack } = useSlideInMenu();
  const { saveEntityToCache } = useEntityCache();

  const { data: fetchedArtistResponse } = useQuery({
    enabled: !!artistId,
    queryKey: ["artistProfileArtist", artistId],
    queryFn: async () => {
      const response = await getArtist(artistId ?? 0);
      const artist = response?.data.payload.results;

      if (artist) {
        saveEntityToCache(artist);
      }
      return response;
    },
    networkMode: "offlineFirst",
  });

  const fetchedArtist = fetchedArtistResponse?.data.payload.results;
  const cacheKey = `${fetchedArtist?.entity_type}-${fetchedArtist?.id}`;
  const { data: artist } = useQuery<Artist>({
    enabled: !!fetchedArtist,
    queryKey: [cacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
  });

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isRefetching,
  } = useArtistComicsAPI(artist?.id ?? 0);

  // Attach scroll handler to the SlideInMenu scroll container
  useEffect(() => {
    const scrollContainer = document.getElementById(
      "slideInMenuScrollContainer",
    );
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (!isFetchingNextPage && hasNextPage) {
        const is500pxAboveScrollBottom =
          scrollContainer.scrollHeight -
            (scrollContainer.scrollTop + scrollContainer.clientHeight) <=
          500;

        if (is500pxAboveScrollBottom) {
          fetchNextPage();
        }
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  // Get bundles directly from the artist data and convert from ImmutableObjects to regular objects
  const bundles: Bundle[] = artist?.bundles
    ? artist.bundles.map((bundle) => bundle as unknown as Bundle)
    : [];

  const hasBundles = bundles.length > 0;

  const orders = useHookstate(appStore.init.comic_library_sort_orders).get({
    noproxy: true,
  });
  const artistSortOrder = useHookstate(artistComicsFilterStore.sortMenus.sortOrder).get();
  const artistSortDuration = useHookstate(artistComicsFilterStore.sortMenus.sortDuration).get();

  const invalidate = useInvalidateQueries(GET_ARTIST_COMICS_QUERY_KEY);

  const [mode] = useState<LayoutStyle["mode"]>("list");

  if (!artist || isLoading || !data)
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        width="100%"
        position="absolute"
        top="0"
        left="0"
      >
        <Spinner color="blaze.blaze" />
      </Flex>
    );

  return (
    <Flex flexDirection="column" height="100%">
      <SlideInHeader
        openMenu={SLIDE_IN_MENUS.publisher}
        onCloseMenu={onCloseMenu}
        handleBack={goBack}
      />
      <Flex
        className="styled-scrollbar"
        flexDirection="column"
        flexGrow={1}
        overflowY="auto"
        pb="24px"
        gap="24px"
      >
        <ArtistProfileHeader artist={artist} onCloseMenu={onCloseMenu} />
        <Flex flexDirection="column" px="24px" gap="24px">
          <Box h="1px" bg="whiteAlpha.200" />
          {artist.description && (
            <ShowMoreSection>
              <Text
                fontSize="14px"
                lineHeight="160%"
                fontWeight={400}
                color="neutral.300"
              >
                {artist.description}
              </Text>
            </ShowMoreSection>
          )}
          {artist.gold_details && (
            <ReleaseTypeBanner
              text={artist.gold_details ?? ""}
              icon={<SealCheckIcon />}
              backgroundColor="blaze.blaze_10"
              topColor="var(--chakra-colors-blaze-blaze_40)"
              bottomColor="var(--chakra-colors-blaze-blaze_20)"
            />
          )}
          {hasBundles ? (
            <Tabs variant="line" colorScheme="blaze">
              <TabList>
                <UnderlinedTab fontSize="16px">Comics</UnderlinedTab>
                <UnderlinedTab fontSize="16px">
                  <Flex alignItems="center" gap={2}>
                    <Text>Bundles</Text>
                    <NewBadge />
                  </Flex>
                </UnderlinedTab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <Flex flexDirection="column" gap="24px">
                    <Flex flexDirection="row" justifyContent="space-between">
                      <SortMenus
                        invalidate={invalidate}
                        sortOrder={artistSortOrder}
                        sortDuration={artistSortDuration}
                        onSortOrderChange={(value) => artistComicsFilterStore.sortMenus.sortOrder.set(value)}
                        onSortDurationChange={(value) => artistComicsFilterStore.sortMenus.sortDuration.set(value)}
                        sortOrders={orders}
                      />
                      {/* <ModeToggle mode={mode} setMode={setMode} /> */}
                    </Flex>
                    <ArtistProfileComicsList
                      data={data}
                      isFetchingNextPage={isFetchingNextPage}
                      isRefetching={isRefetching}
                      mode={mode}
                    />
                  </Flex>
                </TabPanel>
                <TabPanel px={0}>
                  <SlideInMenuBundlesList bundles={bundles} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            <Flex flexDirection="column" gap="24px">
              <Flex flexDirection="row" justifyContent="space-between">
                <SortMenus
                  invalidate={invalidate}
                  sortOrder={artistSortOrder}
                  sortDuration={artistSortDuration}
                  onSortOrderChange={(value) => artistComicsFilterStore.sortMenus.sortOrder.set(value)}
                  onSortDurationChange={(value) => artistComicsFilterStore.sortMenus.sortDuration.set(value)}
                  sortOrders={orders}
                />
                {/* <ModeToggle mode={mode} setMode={setMode} /> */}
              </Flex>
              <Flex direction="column">
                <ArtistProfileComicsList
                  data={data}
                  isFetchingNextPage={isFetchingNextPage}
                  isRefetching={isRefetching}
                  mode={mode}
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
